import {TranslateService} from '@ngx-translate/core';
import {DOCUMENT, Inject, Injectable, Injector} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {DipRegistrationService} from '../../dip-registration.service';
import {Router} from '@angular/router';
import {DipRegistrationErrorsHandler} from '../../dip-registration-error/dip-registration-errors.handler';
import {DipRegistrationCustomerTokenResponse} from '../models/dip-registration-customer-token-response.model';
import {DipRegistrationLoggerService} from './logger/dip-registration-logger.service';
import {DomSanitizer, Meta} from '@angular/platform-browser';
import {DipRegistrationContext} from '../models/dip-registration-context.model';
import {DipRegistrationContextService} from './dip-registration-context.service';
import {DipRegistrationAppContext} from '../models/dip-registration-app-context.model';
import {
  getApiUrl,
  getUrlParameter,
  IScriptAttributes,
  loadScript,
  sanitizeAndValidateUrl
} from './utilities/dip-registration-utility';
import {DipRegistrationNavigationService} from './dip-registration-navigation.service';
import {DipRegistrationUAPState} from '../models/uap/dip-registration-uap-state.model';
import {DipRegistrationTMXInfo} from '../models/dip-registration-tmx-info.model';
import {CookieService} from 'ngx-cookie-service';
import {DipRegistrationBbfpUrls} from '../models/enum/dip-registration-bbfp-urls..enum';
import {DipRegistrationAdobeUrls} from '../models/enum/dip-registration-adobe-urls..enum';
import {DipRegistratioOneTrustUrls} from '../models/enum/dip-registration-onetrust-urls..enum';
import {DipRegistrationTaggingUrls} from '../models/enum/dip-registration-tagging-urls..enum';

import {DipRegistrationCustomerIdentityService} from './dip-registration-customer-identity.service';

declare let window: Window & typeof globalThis & {
  APP_CONTEXT: DipRegistrationAppContext;
  threatmetrix: any;
};

@Injectable()
export class DipRegistrationInitializerService {

  constructor(private translateService: TranslateService,
              private dipRegistrationContextService: DipRegistrationContextService,
              private dipRegistrationService: DipRegistrationService,
              private dipRegistrationNavigationService: DipRegistrationNavigationService,
              private dipRegistrationCustomerIdentityService: DipRegistrationCustomerIdentityService,
              private errorHandler: DipRegistrationErrorsHandler,
              private http: HttpClient,
              private meta: Meta,
              private sanitizer: DomSanitizer,
              private cookieService: CookieService,
              private logger: DipRegistrationLoggerService,
              @Inject(DOCUMENT) private document: Document,
              private injector: Injector) {
  }


  private handleAuthCodeAndUAPState(): [string, DipRegistrationUAPState] {
    return [getUrlParameter('code'), this.dipRegistrationContextService.loadState(getUrlParameter('state'))];
  }

  private async handleUAPRedirect(apiUrl: string, authCode: string): Promise<any> {
    if (authCode) {
      const router = this.injector.get(Router);
      try {
        this.logger.debug(`Auth token is', ${authCode}`);
        await this.dipRegistrationNavigationService.init();
        const customerTokenRes: DipRegistrationCustomerTokenResponse = await this.dipRegistrationService.sendCustomerToken(apiUrl, authCode);
        this.dipRegistrationContextService.setAuthenticated(true);
        if (customerTokenRes?.registered) {
          await router.navigate(['existing-account'], this.dipRegistrationContextService.buildParams());
        } else {
          const dipssGuid = this.dipRegistrationContextService.getAppContext().dipssGuid;
          if (dipssGuid) {
            this.dipRegistrationCustomerIdentityService.create({dipssGuid});
          } else {
            await router.navigate(['customer-identification'], this.dipRegistrationContextService.buildParams());
          }
        }
      } catch (error) {
        await this.errorHandler.handleError(error);
      }
    }
  }

  // check if we are in maintenance mode and we are not skipping this phase with passing skip query params for QA we will show maintenance page
  private processMaintenanceMode() {
    if (this.dipRegistrationContextService.isMaintenanceMode()) {
      window.location.href = this.dipRegistrationContextService.getMaintenancePageUrl();
    }
  }

  async init(): Promise<boolean> {
    this.dipRegistrationContextService.initAppContext();
    const apiUrl = getApiUrl();
    // handle the case when we have a redirect from UAP
    const [authCode, uapState] = await this.handleAuthCodeAndUAPState();
    try {
      const context = await this.dipRegistrationService.initContext(uapState);
      await this.dipRegistrationService.checkSystemAvailability(apiUrl, uapState);
      if (context) {
        this.cookieService.set('TraceabilityID', this.dipRegistrationContextService.getSessionId(), null, '/', '.td.com');
        this.logger.info(`App version ${this.dipRegistrationContextService.getVersionId()}`);
        const language = this.dipRegistrationContextService.getLanguage();
        this.setHtmlLanguageAttribute(language);
        this.processMaintenanceMode();
        let loadingPromises;
        if (!authCode) {
          await this.initializeTMX();
        }
        if (this.dipRegistrationContextService.isDisableThirdPartyScripts()) {
          loadingPromises = [this.setTranslations(language, context)];
        } else {
          loadingPromises =
            [this.setTranslations(language, context),
              this.loadBioCatchScript(context),
              this.initEnsightenScript(context),
              this.initOneTrust(context)];
        }
        await Promise.all(loadingPromises);
        await this.handleUAPRedirect(apiUrl, authCode);
        return true;
      }
      this.logger.error('Fatal error context empty');
    } catch (err) {
      this.logger.error('Error initialising the app', err);
      this.errorHandler.handleMaintenancePageRedirection('APP_INIT_ERROR');
    }
    return false;
  }

  private setHtmlLanguageAttribute(language: string) {
    this.document.documentElement.lang = language === 'fr' ? language : 'en';
  }

  private async setTranslations(language: string, context: DipRegistrationContext) {
    try {
      if (context?.wcmUrl) {
        const translations = await this.getTranslations(context, language);
        if (translations) {
          this.translateService.setTranslation(language, translations, true);
          this.translateService.setDefaultLang(language || 'en_CA');
          this.translateService.use(language || 'en_CA');
          // init phone number
          this.dipRegistrationContextService.setPhoneNumber(this.translateService.instant('DIPRSRegistration.phone.number'));
        }
      } else {
        this.logger.debug('context is empty', context);
      }
    } catch (error) {
      this.logger.error('Fatal error fetching the wcm content');
    }
  }

  private async getTranslations(context: DipRegistrationContext, lang: string) {
    const cachingOptions = context.wcmCacheMaxAge && context.wcmCacheMaxStale ? `&max-age=${context.wcmCacheMaxAge}&max-stale=${context.wcmCacheMaxAge}` : '';
    const contentType = `/TDIGI?Version=All&Province=All&Insurer=All&Form=All&ContentType=DIPRSRegistration${cachingOptions}`;
    const wcmUrl = lang === 'fr' ? `${context.wcmUrl}/fr_CA` : context.wcmUrl;
    this.logger.debug(`WCM url is ${wcmUrl}${contentType}`);
    try {
      const wcmResponse = await this.http.get(`${wcmUrl}${contentType}`).toPromise();
      if (Array.isArray(wcmResponse) && wcmResponse?.length > 0) {
        this.dipRegistrationContextService.setWcmLoaded(true);
        return wcmResponse[0];
      }
      throw new Error('Wcm format is supported');
    } catch (err) {
      try {
        await this.dipRegistrationService.resetContext();
      } catch (error) {
        this.logger.error('Error calling wcm', error);
        // Will send an SPA error to the server
        this.errorHandler.log(error);
      } finally {
        // Will redirect to maintenance page
        this.errorHandler.handleMaintenancePageRedirection('WCM_NOT_AVAILABLE',
          this.dipRegistrationContextService.getVersionId(),
          this.dipRegistrationContextService.getSessionId()
        );
      }
    }
    // We will keep this code just to test our contents in local, in this case we have to comment the other http request above.
    // To test with the mock server : const wcmContent = await this.http.get(`${getApiUrl()}${contentType}`).toPromise();
    // const wcmContent = await this.http.get(`../customer-registration/assets/contents/DIPRSRegistration_${lang}_CA.json`).toPromise();
    // this.dipRegistrationContextService.setWcmLoaded(true);
    // return wcmContent ? wcmContent[0] : null;
  }

  /**
   * load the bio catch script
   * if this fails it should not block the loading of the app
   * @param context the dip registration context
   */
  private async loadBioCatchScript(context: DipRegistrationContext) {
    try {
      const tmxInfo: DipRegistrationTMXInfo = await this.dipRegistrationService.getTmxInfo();
      if (context?.bbfpUrl && tmxInfo?.tmxId) {
        const resolvedBbfpUrl = DipRegistrationBbfpUrls[context.bbfpUrl as keyof typeof DipRegistrationBbfpUrls] ?? null;
        if (!resolvedBbfpUrl) {
          this.logger.error('TMX script URL key is invalid', context.bbfpUrl);
          return;
        }
        this.meta.updateTag({name: 'bcsid', content: tmxInfo.tmxId});
        if (sanitizeAndValidateUrl(this.sanitizer, resolvedBbfpUrl)) {
          await loadScript(resolvedBbfpUrl);
          this.logger.debug('bbfp script is loaded');
          this.logger.debug('bbfpId is ', tmxInfo.tmxId);
        } else {
          this.logger.error('TMX script URL validation failed');
        }
      }
    } catch (error) {
      this.logger.error('TMX script is not loaded', error);
    }

  }

  private async initEnsightenScript(context: DipRegistrationContext) {
    try {
      if (context?.metricsEnabled && context?.adobeLaunchPrivacyUrl) {
        const resolvedTaggingUrl = DipRegistrationTaggingUrls[context.taggingUrl as keyof typeof DipRegistrationTaggingUrls] ?? null;
        if (!resolvedTaggingUrl) {
          this.logger.error('tagging script URL key is invalid', context.taggingUrl);
          return;
        }

        if (sanitizeAndValidateUrl(this.sanitizer, resolvedTaggingUrl)) {
          await loadScript(resolvedTaggingUrl);
          this.logger.debug('taggingUrl script is loaded', context);
        } else {
          this.logger.error('tagging script URL validation failed');
        }
      }
    } catch (error) {
      this.logger.error('Adobe Launch script is not loaded', error);
    }

  }

  /**
   * Get TMXinfo from the Api
   * Initialize TMX with tmxOrgId, tmxId
   */
  private async initializeTMX() {
    try {
      const tmxInfo = await this.dipRegistrationService.buildTmxInfo();
      if (tmxInfo?.tmxOrgId && tmxInfo?.tmxId) {
        window.threatmetrix.profile('tmx.td.com', tmxInfo.tmxOrgId, tmxInfo.tmxId);
        this.logger.debug('TmxId is ', tmxInfo.tmxId);
      } else {
        this.logger.debug('TmxInfo is empty');
      }
    } catch (error) {
      this.errorHandler.log(error);
    }
  }

  private async initOneTrust(context: DipRegistrationContext) {
    try {
      if (context?.metricsEnabled) {
        const resolvedAdobeLaunchPrivacyUrl = DipRegistrationAdobeUrls[context.adobeLaunchPrivacyUrl as keyof typeof DipRegistrationAdobeUrls] ?? null;
        const resolvedOneTrustPrivacyUrl = DipRegistratioOneTrustUrls[context.oneTrustPrivacyUrl as keyof typeof DipRegistratioOneTrustUrls] ?? null;

        let isFirst = true;
        (window as any).OptanonWrapper = async () => {
          if (!isFirst) {
            return;
          }
          isFirst = false;

          if (!resolvedAdobeLaunchPrivacyUrl) {
            this.logger.error('adobeLaunchPrivacyUrl script URL key is invalid', context.adobeLaunchPrivacyUrl);
            return;
          }

          if (sanitizeAndValidateUrl(this.sanitizer, resolvedAdobeLaunchPrivacyUrl)) {
            await loadScript(resolvedAdobeLaunchPrivacyUrl);
          } else {
            this.logger.error('adobeLaunchPrivacyUrl script URL validation failed');
          }
        };

        const attributes: IScriptAttributes[] = [{
          name: 'data-domain-script',
          value: context?.oneTrustPrivacyKey
        }, {
          name: 'data-document-language',
          value: 'true'
        }];

        if (!resolvedOneTrustPrivacyUrl) {
          this.logger.error('oneTrustPrivacyUrl script URL key is invalid', context.oneTrustPrivacyUrl);
          return;
        }

        if (sanitizeAndValidateUrl(this.sanitizer, resolvedOneTrustPrivacyUrl)) {
          await loadScript(resolvedOneTrustPrivacyUrl, attributes);
        } else {
          this.logger.error('oneTrustPrivacyUrl script URL validation failed');
        }
      }
    } catch (error) {
      this.logger.error('One Trust & tagging script is not loaded', error);
    }
  }
}
